import { LightningBoltIcon } from '@heroicons/react/outline';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';

import clsxm from '@/lib/clsxm';

import { creditsLeft, useDashboardStats } from '@/components/dashboard/credits';
import { SimpleTooltip } from '@/components/ui/tooltip';

import { useUser } from '@/utils/useUser';

import { nav, type NavVariant, useNavTrack } from './navUi';

/**
 * True from the given width up: the sidebar (768 px) and the rail (1024 px)
 * are in the page below that too, just hidden, and shouldn't fetch there.
 */
function useMinWidth(px: number) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const query = window.matchMedia(`(min-width: ${px}px)`);
    const update = () => setMatches(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, [px]);
  return matches;
}

/**
 * Credits left for Free-plan users, from the dashboard chip's request. Other
 * plans see their plan name on the account button instead, so the stats
 * request only runs for Free accounts, and only where the meter is visible.
 */
function useFreeCredits(variant: NavVariant) {
  const { t, lang } = useLocale('dashboard');
  const { user, isFreePlan } = useUser();
  // The drawer is only mounted while open.
  const visible = useMinWidth(variant === 'rail' ? 1024 : 768);
  const enabled = !!user && isFreePlan && (variant === 'drawer' || visible);
  const { data, isError } = useDashboardStats(enabled);
  const credits = data ? creditsLeft(data) : null;
  let label: string | null = null;
  if (credits) {
    label =
      credits.left === null
        ? t('Unlimited credits')
        : credits.left === 1
          ? t('1 credit left')
          : t('{{credits}} credits left', {
              credits: new Intl.NumberFormat(lang).format(credits.left),
            });
  }
  return { enabled, credits, label, isError };
}

const upgradeClass =
  'flex h-7 shrink-0 items-center rounded-full bg-primary-600 px-3 text-[12px] font-semibold text-white transition-colors hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600';

/** The sidebar's and drawer's quiet meter: credits left, a bar, Upgrade. */
export function CreditsCard({
  variant,
  className,
}: {
  variant: NavVariant;
  /** On a wrapper that renders only with the card. */
  className?: string;
}) {
  const { t } = useLocale('dashboard');
  const track = useNavTrack(variant);
  const { enabled, credits, label, isError } = useFreeCredits(variant);
  if (!enabled) return null;

  const card = (
    <div className={clsxm('rounded-xl px-3 py-2.5', nav.soft)}>
      <div className='flex items-center justify-between gap-2'>
        {isError ? (
          <span className={clsxm('text-[13px] font-medium', nav.ink)}>
            {t('Free plan')}
          </span>
        ) : label ? (
          <Link
            href='/settings/usage'
            onClick={() => track('credits', '/settings/usage')}
            className={clsxm(
              // Wraps rather than truncates: "Te quedan 3 créditos" next to
              // "Mejorar plan" doesn't fit one line in 216 px.
              'min-w-0 rounded text-[13px] font-medium leading-[18px] tabular-nums hover:underline',
              nav.ink,
              nav.focus,
            )}
          >
            {label}
          </Link>
        ) : (
          <span
            aria-hidden='true'
            className='h-3.5 w-24 animate-pulse rounded-full bg-[#0f0f28]/[0.07] dark:bg-white/10'
          />
        )}
        <Link
          href='/settings/billing'
          onClick={() => track('upgrade', '/settings/billing')}
          className={upgradeClass}
        >
          {t('Upgrade')}
        </Link>
      </div>
      {!isError && (
        <span
          aria-hidden='true'
          className='relative mt-2 block h-1.5 overflow-hidden rounded-full bg-[#0f0f28]/[0.08] dark:bg-white/10'
        >
          {credits && (
            <span
              style={{ width: `${Math.round(credits.fraction * 100)}%` }}
              className={clsxm(
                'absolute inset-y-0 start-0 rounded-full',
                credits.low ? 'bg-amber-500' : 'bg-primary-500',
              )}
            />
          )}
        </span>
      )}
    </div>
  );
  return className ? <div className={className}>{card}</div> : card;
}

const RING = 2 * Math.PI * 17;

/** The rail's version: a bolt inside a ring of credits left, linking to Upgrade. */
export function CreditsRing() {
  const { t } = useLocale('dashboard');
  const track = useNavTrack('rail');
  const { enabled, credits, label } = useFreeCredits('rail');
  if (!enabled) return null;
  const tip = label ? `${label} · ${t('Upgrade plan')}` : t('Upgrade plan');

  return (
    <SimpleTooltip title={tip} side='right'>
      <Link
        href='/settings/billing'
        aria-label={tip}
        onClick={() => track('upgrade', '/settings/billing')}
        className={clsxm(
          'relative flex h-10 w-10 items-center justify-center rounded-xl text-primary-600 transition-colors hover:bg-primary-50 dark:text-primary-300 dark:hover:bg-primary-500/15',
          nav.focus,
        )}
      >
        <svg
          aria-hidden='true'
          viewBox='0 0 40 40'
          className='absolute inset-0 h-10 w-10 -rotate-90 rtl:-scale-y-100'
        >
          <circle
            cx='20'
            cy='20'
            r='17'
            fill='none'
            strokeWidth='2.5'
            className='stroke-[#0f0f28]/[0.08] dark:stroke-white/10'
          />
          {credits && (
            <circle
              cx='20'
              cy='20'
              r='17'
              fill='none'
              strokeWidth='2.5'
              strokeLinecap='round'
              strokeDasharray={`${RING * credits.fraction} ${RING}`}
              className={
                credits.low ? 'stroke-amber-500' : 'stroke-primary-500'
              }
            />
          )}
        </svg>
        <LightningBoltIcon className='h-[18px] w-[18px]' aria-hidden='true' />
      </Link>
    </SimpleTooltip>
  );
}
